
  // Enmascara el email mostrando solo los primeros caracteres antes del @ (máx 6) y '***'
  const maskEmail = (email) => {
    if (!email) return 'Usuario';
    const emailStr = String(email);
    const atIdx = emailStr.indexOf('@');
    if (atIdx === -1) return emailStr.slice(0, 6) + '***';
    const visible = emailStr.slice(0, Math.min(6, atIdx));
    return visible + '***';
  };
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuinielaStore } from '../store/quinielaStore'
import { useAuthStore } from '../store/authStore'
import { useWalletStore } from '../store/walletStore'
import { 
  Calendar, 
  DollarSign, 
  Users, 
  Trophy, 
  Clock, 
  Loader, 
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  Target,
  Wallet,
  X
} from 'lucide-react'
import toast from 'react-hot-toast'

export default function QuinielaDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { fetchQuinielaById, joinQuiniela, checkUserParticipation, getParticipants, getQuinielaMatches, loading } = useQuinielaStore()
    const [matches, setMatches] = useState([])
  const { fetchBalance, balance } = useWalletStore()
  
  const [quiniela, setQuiniela] = useState(null)
  const [participations, setParticipations] = useState([])
  const [participants, setParticipants] = useState([])
  const [joiningQuiniela, setJoiningQuiniela] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    console.log('QuinielaDetail mounted with ID:', id)
    if (id) {
      loadQuiniela()
    } else {
      console.error('No quiniela ID provided')
      toast.error('ID de quiniela no válido')
      navigate('/quinielas')
    }
    
    // Cargar balance del usuario
    if (user) {
      fetchBalance()
    }
  }, [id, user])

  const loadQuiniela = async () => {
    console.log('Loading quiniela with ID:', id)
    try {
      const result = await fetchQuinielaById(id)
      console.log('Quiniela fetch result:', result)
      
      if (result.success) {
        console.log('Quiniela loaded successfully:', result.data)
        setQuiniela(result.data)
        // Cargar partidos de la quiniela
        const matchesResult = await getQuinielaMatches(id)
        if (matchesResult.success) {
          setMatches(matchesResult.matches)
        }
        // Verificar si el usuario ya se unió a esta quiniela
        await checkIfUserJoined()
        // Cargar participantes
        await loadParticipants()
      } else {
        console.error('Failed to load quiniela:', result.error)
        toast.error('No se pudo cargar la quiniela')
        navigate('/quinielas')
      }
    } catch (error) {
      console.error('Error in loadQuiniela:', error)
      toast.error('Error al cargar la quiniela')
      navigate('/quinielas')
    }
  }

  const loadParticipants = async () => {
    const result = await getParticipants(id)
    if (result.success) {
      // Ordenar por puntos (descendente) y fecha de inscripción
      const sorted = result.participants.sort((a, b) => {
        if (b.points !== a.points) return (b.points || 0) - (a.points || 0)
        return (a.joinedAt?.seconds || 0) - (b.joinedAt?.seconds || 0)
      })
      setParticipants(sorted)
    }
  }

  const checkIfUserJoined = async () => {
    if (!user) return
    
    const result = await checkUserParticipation(id, user.uid)
    if (result.isParticipant) {
      setParticipations(result.participations)
    }
  }

  const handleJoinQuiniela = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para unirte')
      navigate('/login')
      return
    }

    // Verificar saldo suficiente
    if (balance.total < quiniela.entryFee) {
      toast.error('Saldo insuficiente. Por favor, deposita fondos a tu wallet.')
      return
    }

    // Mostrar modal de confirmación
    setShowConfirmModal(true)
  }

  const confirmJoin = async () => {
    setShowConfirmModal(false)
    setJoiningQuiniela(true)
    const result = await joinQuiniela(id, user.uid, user.username || user.email)
    setJoiningQuiniela(false)

    if (result.success) {
      // Recargar balance y quiniela
      await fetchBalance()
      await loadQuiniela()
    }
  }

  const getStatusBadge = (status) => {
    const badges = {
      upcoming: { text: 'Próxima', className: 'bg-[#0F3D2E] text-[#F8F8F8]' },
      open: { text: 'Abierta', className: 'bg-[#0F3D2E] text-[#F8F8F8]' },
      in_progress: { text: 'En Curso', className: 'bg-[#D4AF37] text-[#1A1A1A]' },
      finished: { text: 'Finalizada', className: 'bg-[#222222] text-[#F8F8F8]' },
      cancelled: { text: 'Cancelada', className: 'bg-[#B11217] text-[#F8F8F8]' }
    }
    const badge = badges[status] || badges.upcoming
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.className}`}>
        {badge.text}
      </span>
    )
  }

  const canJoin = () => {
    if (!quiniela) return false
    
    const now = new Date()
    const closeDate = quiniela.registrationCloseDate?.toDate?.() || new Date(quiniela.registrationCloseDate)
    
    const isOpen = quiniela.status === 'upcoming' || quiniela.status === 'open'
    const beforeCloseDate = now < closeDate
    const hasSpace = !quiniela.participants.maxLimit || 
                      quiniela.participants.count < quiniela.participants.maxLimit
    
    return isOpen && beforeCloseDate && hasSpace
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return ''
    const date = timestamp?.toDate?.() || new Date(timestamp)
    return date.toLocaleDateString('es-MX', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTimeRemaining = () => {
    if (!quiniela) return null
    
    const now = new Date()
    const closeDate = quiniela.registrationCloseDate?.toDate?.() || new Date(quiniela.registrationCloseDate)
    const diff = closeDate - now
    
    if (diff <= 0) return 'Cerrado'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  if (loading || !quiniela) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-[#22C55E]" size={40} />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
      {/* Back Button */}
      <Link
        to="/quinielas"
        className="inline-flex items-center gap-2 text-[#F8F8F8] hover:text-[#D4AF37] mb-6"
      >
        <ArrowLeft size={20} />
        Volver a Quinielas
      </Link>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">{quiniela.name}</h1>
            {quiniela.description && (
              <p className="text-[#F8F8F8]">{quiniela.description}</p>
            )}
          </div>
          {getStatusBadge(quiniela.status)}
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-[#F8F8F8]">
          <span className="flex items-center gap-1">
            <Trophy size={16} />
            {quiniela.league.replace('_', ' ').toUpperCase()}
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={16} />
            Temporada {quiniela.season}
          </span>
          <span className="flex items-center gap-1">
            <Target size={16} />
            Jornada {quiniela.jornada}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Inscripción */}
          <div className="card bg-gradient-to-br from-[#22C55E]/20 to-[#D4AF37]/10 border-2 border-[#0F3D2E]">
            <h2 className="text-xl font-semibold mb-4 text-[#D4AF37]">Inscripción</h2>
            
            <div className="space-y-4">
              <div className="text-center py-4">
                <p className="text-sm text-[#F8F8F8] mb-2">Costo de Entrada</p>
                <p className="text-4xl font-bold text-[#D4AF37]">
                  ${quiniela.entryFee.toFixed(2)}
                </p>
                <p className="text-xs text-[#F8F8F8] mt-1">MXN</p>
              </div>

              {user && (
                <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#F8F8F8] flex items-center gap-2">
                      <Wallet size={16} />
                      Tu saldo:
                    </span>
                    <span className={`font-semibold ${balance.total >= quiniela.entryFee ? 'text-[#22C55E]' : 'text-[#B11217]'}`}>
                      ${balance.total.toFixed(2)}
                    </span>
                  </div>
                  {balance.total < quiniela.entryFee && canJoin() && (
                    <p className="text-xs text-[#B11217] mt-2 flex items-center gap-1">
                      <AlertCircle size={12} />
                      Saldo insuficiente
                    </p>
                  )}
                </div>
              )}

              {/* Mostrar participaciones existentes */}
              {participations.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#D4AF37]">
                    Tus inscripciones ({participations.length}):
                  </p>
                  {participations.map((participation, index) => (
                    <div 
                      key={participation.id}
                      className="bg-[#0F3D2E]/20 border border-[#0F3D2E] rounded-lg p-3"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-[#22C55E]">
                          #{index + 1} - {participation.points || 0} pts
                        </span>
                        <CheckCircle size={16} className="text-[#22C55E]" />
                      </div>
                      <Link
                        to={`/quinielas/${id}/predictions/${participation.id}`}
                        className={
                          `btn w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ` +
                          ((!participation.predictions || Object.keys(participation.predictions).length === 0)
                            ? 'bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C19F2E] border-2 border-[#D4AF37] shadow-lg py-4 font-bold text-lg'
                            : 'bg-[#222222] text-[#F8F8F8] border border-[#0F3D2E] hover:bg-[#333333] py-4 font-bold text-lg')
                        }
                        style={{ fontSize: '1.25rem', minHeight: '56px' }}
                      >
                        {(!participation.predictions || Object.keys(participation.predictions).length === 0)
                          ? 'Realiza tus predicciones'
                          : 'Ver Predicciones'}
                      </Link>
                    </div>
                  ))}
                </div>
              )}

              {canJoin() ? (
                <button
                  onClick={handleJoinQuiniela}
                  disabled={joiningQuiniela || (user && balance.total < quiniela.entryFee)}
                  className="btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12] w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {joiningQuiniela ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      Procesando...
                    </>
                  ) : participations.length > 0 ? (
                    'Comprar Otra Entrada'
                  ) : (
                    'Unirse Ahora'
                  )}
                </button>
              ) : !canJoin() && participations.length === 0 && (
                <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
                  <p className="text-sm text-[#F8F8F8] flex items-center gap-2">
                    <AlertCircle size={16} />
                    Inscripciones cerradas
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Información de Fechas */}
          <div className="card bg-[#222222] border border-[#0F3D2E]">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#D4AF37]">
              <Clock size={20} />
              Fechas Importantes
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-[#0F3D2E]">
                <span className="text-[#F8F8F8]">Primer Partido:</span>
                <span className="font-medium text-[#F8F8F8]">{formatDate(quiniela.startDate)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#0F3D2E]">
                <span className="text-[#F8F8F8]">Último Partido:</span>
                <span className="font-medium text-[#F8F8F8]">{formatDate(quiniela.endDate)}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-[#F8F8F8]">Cierre de Registro:</span>
                <span className="font-medium text-[#B11217]">
                  {formatDate(quiniela.registrationCloseDate)}
                </span>
              </div>
              {canJoin() && (
                <div className="bg-[#D4AF37]/20 border border-[#D4AF37] rounded-lg p-3 mt-4">
                  <p className="text-sm text-[#D4AF37] flex items-center gap-2">
                    <Clock size={16} />
                    <span>
                      <strong>Tiempo restante:</strong> {getTimeRemaining()}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Partidos */}
          <div className="card bg-[#222222] border border-[#0F3D2E]">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#D4AF37]">
              <Trophy size={20} />
              Partidos ({matches.length})
            </h2>
            {matches.length === 0 ? (
              <div className="bg-[#0F3D2E]/20 border border-[#0F3D2E] rounded-lg p-4">
                <p className="text-sm text-[#F8F8F8]">
                  No hay partidos registrados para esta quiniela.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-[#F8F8F8]">
                  <thead>
                    <tr className="bg-[#0F3D2E]">
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Logo</th>
                      <th className="px-3 py-2 text-left">Local</th>
                      <th className="px-3 py-2 text-left">Logo</th>
                      <th className="px-3 py-2 text-left">Visitante</th>
                      <th className="px-3 py-2 text-left">Estadio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.map((match) => (
                      <tr key={match.id} className="border-b border-[#0F3D2E]">
                        <td className="px-3 py-2">{match.date ? new Date(match.date).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {match.teams?.home?.logo ? (
                            <img src={match.teams.home.logo} alt="logo local" className="w-6 h-6 object-contain mx-auto" />
                          ) : '-'}
                        </td>
                        <td className="px-3 py-2">{match.teams?.home?.name || match.homeTeam || match.home_name || match.local || match.home || match.equipoLocal || '-'}</td>
                        <td className="px-3 py-2 text-center">
                          {match.teams?.away?.logo ? (
                            <img src={match.teams.away.logo} alt="logo visitante" className="w-6 h-6 object-contain mx-auto" />
                          ) : '-'}
                        </td>
                        <td className="px-3 py-2">{match.teams?.away?.name || match.awayTeam || match.away_name || match.visitante || match.away || match.equipoVisitante || '-'}</td>
                        <td className="px-3 py-2">{match.venue || match.stadium || match.estadio || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Participantes */}
          <div className="card bg-[#222222] border border-[#0F3D2E]">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#D4AF37]">
              <Users size={20} />
              Participantes
            </h2>
            
            {/* Resumen */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-[#F8F8F8]">Inscritos:</span>
                <span className="font-bold text-2xl text-[#F8F8F8]">
                  {quiniela.participants.count}
                </span>
              </div>
              {quiniela.participants.maxLimit && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#F8F8F8]">Límite:</span>
                  <span className="font-medium text-[#F8F8F8]">
                    {quiniela.participants.maxLimit}
                  </span>
                </div>
              )}
              <div className="bg-[#1A1A1A] rounded-full h-2 overflow-hidden">
                <div
                  className="bg-[#0F3D2E] h-full transition-all"
                  style={{
                    width: quiniela.participants.maxLimit 
                      ? `${(quiniela.participants.count / quiniela.participants.maxLimit) * 100}%`
                      : '0%'
                  }}
                />
              </div>
            </div>

            {/* Lista de Participantes */}
            {participants.length > 0 && (
              <div className="mt-4 border-t border-[#0F3D2E] pt-4">
                <h3 className="font-semibold text-sm text-[#D4AF37] mb-3">
                  {quiniela.status === 'finished' ? 'Tabla de Posiciones Final' : 'Tabla de Posiciones'}
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {participants.map((participant, index) => (
                    <div 
                      key={participant.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        quiniela.status === 'finished' && participant.position === 1
                          ? 'bg-[#D4AF37]/20 border-2 border-[#D4AF37]'
                          : quiniela.status === 'finished' && participant.position === 2
                          ? 'bg-[#222222] border-2 border-[#F8F8F8]'
                          : quiniela.status === 'finished' && participant.position === 3
                          ? 'bg-[#B11217]/20 border-2 border-[#B11217]'
                          : 'bg-[#222222] border border-[#0F3D2E]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {quiniela.status === 'finished' && participant.position && (
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#1A1A1A] font-bold text-sm">
                            {participant.position === 1 ? '🥇' : 
                             participant.position === 2 ? '🥈' : 
                             participant.position === 3 ? '🥉' : 
                             `#${participant.position}`}
                          </div>
                        )}
                        {quiniela.status !== 'finished' && (
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#0F3D2E] font-bold text-sm text-[#F8F8F8]">
                            #{index + 1}
                          </div>
                        )}
                        <div>
                          {(() => {
                            const isEmail = (str) => typeof str === 'string' && str.includes('@');
                            const displayName = participant.username && isEmail(participant.username)
                              ? maskEmail(participant.username)
                              : participant.username
                                ? participant.username
                                : maskEmail(participant.email);
                            return (
                              <p className="font-medium text-[#F8F8F8]">
                                {displayName}
                              </p>
                            );
                          })()}
                          {quiniela.status === 'finished' && participant.prize > 0 && (
                            <p className="text-sm text-[#22C55E] font-semibold">
                              Premio: ${participant.prize}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg text-[#F8F8F8]">
                          {participant.points || 0} <span className="text-sm text-[#F8F8F8]">pts</span>
                        </p>
                        {participant.predictions && (
                          <p className="text-xs text-[#F8F8F8]">
                            {Object.keys(participant.predictions).length} predicciones
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Premios */}
          <div className="card bg-gradient-to-br from-[#D4AF37]/10 to-[#B11217]/10 border-2 border-[#D4AF37]">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-[#D4AF37]">
              <Trophy size={20} className="text-[#D4AF37]" />
              Premios
            </h2>
            <div className="space-y-3">
              <div className="text-center py-3 bg-[#222222] rounded-lg border border-[#0F3D2E]">
                <p className="text-sm text-[#F8F8F8] mb-1">Bolsa Total Acumulada</p>
                <p className="text-3xl font-bold text-[#D4AF37]">
                  ${quiniela.prizePool.toFixed(2)}
                </p>
              </div>
              
              {quiniela.prizePool > 0 && (
                <>
                  <div className="flex justify-between items-center bg-[#222222] rounded-lg p-3 border border-[#0F3D2E]">
                    <span className="text-sm text-[#F8F8F8]">🥇 1er Lugar (80%)</span>
                    <span className="font-bold text-[#D4AF37]">
                      ${quiniela.prizes.first.toFixed(0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-[#222222] rounded-lg p-3 border border-[#0F3D2E]">
                    <span className="text-sm text-[#F8F8F8]">🥈 2do Lugar (15%)</span>
                    <span className="font-bold text-[#22C55E]">
                      ${quiniela.prizes.second.toFixed(0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center bg-[#222222] rounded-lg p-3 border border-[#0F3D2E]">
                    <span className="text-sm text-[#F8F8F8]">🥉 3er Lugar (5%)</span>
                    <span className="font-bold text-[#F8F8F8]">
                      ${quiniela.prizes.third.toFixed(0)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación de Pago */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg max-w-md w-full p-6 relative">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="absolute top-4 right-4 text-[#F8F8F8] hover:text-[#D4AF37]"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-[#D4AF37]">
                <DollarSign size={32} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold text-[#D4AF37] mb-2">
                Confirmar Inscripción
              </h3>
              <p className="text-[#F8F8F8]">
                {quiniela.name}
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-[#1A1A1A] border border-[#0F3D2E] rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#F8F8F8]">Costo de entrada:</span>
                  <span className="font-bold text-lg text-[#D4AF37]">
                    ${quiniela.entryFee.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#F8F8F8]">Tu saldo actual:</span>
                  <span className="font-semibold text-[#22C55E]">
                    ${balance.total.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-[#0F3D2E] pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#F8F8F8]">Saldo después:</span>
                    <span className="font-bold text-lg text-[#F8F8F8]">
                      ${(balance.total - quiniela.entryFee).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-[#0F3D2E]/20 border border-[#0F3D2E] rounded-lg p-3">
                <p className="text-sm text-[#F8F8F8]">
                  <strong>Nota:</strong> El pago se deducirá automáticamente de tu wallet. 
                  Si tienes saldo depositado, se usará primero ese saldo.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 btn bg-[#1A1A1A] text-[#F8F8F8] hover:bg-[#333333] border border-[#0F3D2E]"
              >
                Cancelar
              </button>
              <button
                onClick={confirmJoin}
                className="flex-1 btn bg-[#B11217] text-[#F8F8F8] hover:bg-[#8B0E12]"
              >
                Confirmar Pago
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
